import React, { useRef } from "react"
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faClose,
  faImages,
  faLock,
  faLink,
  faPen,
  faTrashCan,
  faUpload,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import Button, { ButtonIcon } from "../Button/Button"
import ModalPortal from "../Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { useImageLibrary } from "../../hooks/ImageLibrary/useImageLibrary"
import { IMAGE_PROFILES, ImageProfileKey } from "../../constants/imageProfiles"
import { InputPhotoCropModal } from "../InputPhoto/InputPhotoCropModal"
import { ExternalImageUrlsPopup } from "./ExternalImageUrlsPopup"
import { EImageAssetSourceType } from "../../Enum/EImageAssetSourceType"

interface ImageLibraryPickerProps {
  disabled?: boolean
  imageProfile?: ImageProfileKey
  maxSelection?: number
  selectedIds: number[]
  selectedUrls: string[]
  onChange: (ids: number[], urls: string[]) => void | Promise<void>
}

export const ImageLibraryPicker: React.FC<ImageLibraryPickerProps> = ({
  disabled = false,
  imageProfile = "catalogProduct",
  maxSelection,
  selectedIds,
  selectedUrls,
  onChange,
}) => {
  const { t } = useTranslation("catalogProductEntity")

  const inputRef = useRef<HTMLInputElement>(null)

  const [isExternalPopupOpen, setIsExternalPopupOpen] = React.useState(false)

  const profile = IMAGE_PROFILES[imageProfile] ?? IMAGE_PROFILES.catalogProduct

  const library = useImageLibrary({ enabled: !disabled, maxSelection, selectedIds, selectedUrls, onChange })

  return (
    <>
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <section className=" p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-bold text-slate-800">{t("label_image_library")}</h3>

              <p className="text-xs text-slate-500">{t("label_image_library_hint")}</p>
            </div>

            {!disabled && (
              <ButtonIcon
                typeButton="button"
                icon={faImages}
                size={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGPlusColor}
                onClick={library.handleOpen}
                descriptionKey="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.libraryHandleOpen"
              />
            )}
          </div>

          {selectedUrls.length > 0 && (
            <div className="mt-3 flex gap-2 overflow-x-auto p-4">
              {selectedUrls.map((url, index) => (
                <div key={`${url}-${index}`} className="relative shrink-0">
                  <img
                    src={url.toClientRequest()}
                    alt={t("label_image_position", { index: index + 1 })}
                    className="h-20 w-20 rounded-lg object-cover"
                  />

                  {!disabled && (
                    <>
                      {library.selectedAssetsMetadataLoaded &&
                        (library.assets.find((asset) => asset.url === url)?.sourceType ===
                        EImageAssetSourceType.Edited ? (
                          <span
                            title={t("label_image_already_edited")}
                            aria-label={t("label_image_already_edited")}
                            className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-slate-500 text-[10px] text-white shadow"
                          >
                            <FontAwesomeIcon icon={faLock} />
                          </span>
                        ) : (
                          <button
                            type="button"
                            aria-label={t("button_edit_image_crop")}
                            onClick={() => library.handleEditSelectedImage(index)}
                            className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-slate-900 text-[10px] text-white shadow transition-colors hover:bg-orange-600"
                            data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.editImageCrop"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        ))}

                      <button
                        type="button"
                        aria-label={t("button_remove_image_from_product")}
                        onClick={() =>
                          onChange(
                            selectedIds.filter((_, selectedIndex) => selectedIndex !== index),
                            selectedUrls.filter((_, selectedIndex) => selectedIndex !== index),
                          )
                        }
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-red-600 text-[10px] text-white shadow"
                        data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.removeImageFromProduct"
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </Animate>

      {library.isOpen && (
        <ModalPortal>
          <div className={defaultStyle.overlay}>
            <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
              <section className={defaultStyle.alert2}>
                <TitlePageResponsiveIconSmall
                  title={t("label_image_library")}
                  icon={faImages}
                  iconColor={THEME.colors.iconBGColor}
                  children={
                    <div className={defaultStyle.ButtonsActions}>
                      <ButtonIcon
                        sizeNumber={SIZE.buttonCloseIconSize}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.buttonFGPlusColor}
                        icon={faLink}
                        onClick={() => setIsExternalPopupOpen(true)}
                        size="small"
                        descriptionKey="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.setIsExternalPopupOpenTrue"
                      />

                      <div className="w-2" />

                      <ButtonIcon
                        sizeNumber={SIZE.buttonCloseIconSize}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.buttonFGPlusColor}
                        icon={faUpload}
                        onClick={() => inputRef.current?.click()}
                        size="small"
                        descriptionKey="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.inputRefCurrent"
                      />

                      <div className="w-2" />

                      <ButtonIcon
                        sizeNumber={SIZE.buttonCloseIconSize}
                        bgColor={THEME.colors.buttonBGCloseColor}
                        iconColor={THEME.colors.buttonFGCloseColor}
                        icon={faClose}
                        onClick={library.handleClose}
                        size="small"
                        descriptionKey="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.libraryHandleClose"
                      />
                    </div>
                  }
                />

                <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                  <div className="p-2">
                    <input
                      ref={inputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(event) => {
                        library.handleUpload(Array.from(event.target.files ?? []))
                        event.target.value = ""
                      }}
                      data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.input.input"
                    />

                    <div className="rounded-lg border border-orange-100 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-black text-slate-800">
                            {t("label_image_library_selection_title")}
                          </p>

                          <p className="text-xs text-slate-500">{t("label_image_library_selection_hint")}</p>
                        </div>

                        <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-black text-white">
                          {library.selectedIds.length}/{library.maxProductImages}
                        </span>
                      </div>
                    </div>

                    <div className={defaultStyle.divisionBar}></div>

                    {library.selectedAssets.length > 0 && (
                      <div className="mt-2 p-3">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-black text-slate-800">{t("label_image_order")}</h3>

                            <p className="text-xs text-slate-500">{t("label_image_order_hint")}</p>
                          </div>
                        </div>

                        <div className="flex gap-3 overflow-x-auto">
                          {library.selectedAssets.map((asset, index) => (
                            <div key={asset.id} className="w-28 shrink-0  bg-white p-2">
                              <img
                                src={asset.url.toClientRequest()}
                                alt={t("label_image_position", { index: index + 1 })}
                                className="aspect-square w-full rounded-lg object-cover"
                              />

                              <div className="mt-2 flex justify-center gap-2">
                                {asset.sourceType === EImageAssetSourceType.Edited ? (
                                  <span
                                    title={t("label_image_already_edited")}
                                    aria-label={t("label_image_already_edited")}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-200 text-slate-500"
                                  >
                                    <FontAwesomeIcon icon={faLock} />
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => library.handleEditAsset(asset, index)}
                                    aria-label={t("button_edit_image_crop")}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white transition-colors hover:bg-orange-600"
                                    data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.editImageCrop2"
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                  </button>
                                )}

                                <button
                                  type="button"
                                  disabled={index === 0}
                                  onClick={() => library.handleMove(index, -1)}
                                  aria-label={t("button_move_image_left")}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 transition-colors hover:bg-orange-100 hover:text-orange-700 disabled:opacity-30"
                                  data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.moveImageLeft"
                                >
                                  <FontAwesomeIcon icon={faArrowLeft} />
                                </button>

                                <button
                                  type="button"
                                  disabled={index === library.selectedAssets.length - 1}
                                  onClick={() => library.handleMove(index, 1)}
                                  aria-label={t("button_move_image_right")}
                                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 transition-colors hover:bg-orange-100 hover:text-orange-700 disabled:opacity-30"
                                  data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.moveImageRight"
                                >
                                  <FontAwesomeIcon icon={faArrowRight} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={defaultStyle.divisionBar}></div>

                    <div className="flex gap-3 overflow-x-auto p-3">
                      {library.assets.map((asset) => {
                        const selected = library.selectedIds.includes(asset.id)

                        const selectedOrder = library.selectedIds.indexOf(asset.id) + 1

                        return (
                          <div
                            key={asset.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => library.handleToggle(asset)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                library.handleToggle(asset)
                              }
                            }}
                            className={`group relative w-32 shrink-0 overflow-hidden rounded-xl border-4 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:w-36 ${
                              selected
                                ? "border-emerald-500 ring-4 ring-emerald-100"
                                : "border-white ring-1 ring-slate-200 hover:border-orange-300"
                            }`}
                            data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.libraryHandleToggleAsset"
                          >
                            <img
                              src={asset.url.toClientRequest()}
                              alt={t("label_image_library")}
                              className={`aspect-square w-full object-cover transition-all duration-300 ${selected ? "brightness-90" : "group-hover:scale-105"}`}
                            />

                            <button
                              type="button"
                              aria-label={t("button_remove_image")}
                              onClick={(event) => {
                                event.stopPropagation()
                                library.handleRemove(asset)
                              }}
                              className="absolute bottom-2 right-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-red-600 text-white shadow-lg transition-colors hover:bg-red-700"
                              data-interaction-description-key="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.removeImage"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </button>

                            {selected && (
                              <>
                                <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-emerald-600 text-white shadow-lg">
                                  <FontAwesomeIcon icon={faCheck} />
                                </span>

                                <span className="absolute right-2 top-2 rounded-full border-2 border-white bg-slate-900 px-2.5 py-1 text-xs font-black text-white shadow-lg">
                                  {selectedOrder}
                                </span>

                                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/90 to-transparent px-2 pb-2 pt-7 text-left text-[10px] font-black uppercase tracking-wider text-white">
                                  {t("label_image_selected_order", { index: selectedOrder })}
                                </span>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    {library.assets.length === 0 && (
                      <p className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
                        {t("label_image_library_empty")}
                      </p>
                    )}

                    <div className={defaultStyle.divisionBar}></div>

                    <div className={defaultStyle.buttonsController}>
                      <Button
                        typeButton="button"
                        label={t("button_finish_images")}
                        bgColor={THEME.colors.buttonBGSaveColor}
                        onClick={library.handleConfirm}
                        descriptionKey="interactionDescriptions:components.imageLibrary.imageLibraryPicker.button.finishImages"
                      />
                    </div>
                  </div>
                </Animate>
              </section>
            </Animate>
          </div>
        </ModalPortal>
      )}

      {library.cropState && (
        <InputPhotoCropModal
          cropState={library.cropState}
          profile={profile}
          onCancel={library.handleCancelCrop}
          onConfirm={library.handleConfirmCrop}
        />
      )}

      {isExternalPopupOpen && (
        <ExternalImageUrlsPopup
          onClose={() => setIsExternalPopupOpen(false)}
          onConfirm={library.handleAddExternalUrls}
        />
      )}
    </>
  )
}
